import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { VisualContext } from '../VisualContext';
import { QuizQuestion } from '../data/questions';

type QuizInterfaceProps = {
  activeQuestion: QuizQuestion;
  trackId: string;
  progress: number;
  onAnswer: (questionId: string, optionId: string) => void;
  onBack: () => void;
};

export const QuizInterface: React.FC<QuizInterfaceProps> = ({ 
  activeQuestion, 
  trackId, 
  progress, 
  onAnswer,
  onBack
}) => {
  return (
    <div className="flex flex-col lg:flex-row min-h-[600px] bg-white border-2 border-black rounded-2xl overflow-hidden shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
      
      {/* === MOBILE VISUAL (Only visible on screens smaller than lg) === */}
      <div className="lg:hidden w-full h-48 bg-slate-100 border-b-2 border-slate-100 relative">
         <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:20px_20px]" />
         <VisualContext questionId={activeQuestion.id} trackId={trackId} className="p-0" />
      </div>

      {/* LEFT: Interaction Area */}
      <div className="flex-1 p-6 md:p-10 flex flex-col relative">
         
         {/* Back Button */}
         <button 
           onClick={onBack}
           className="absolute top-6 left-6 text-slate-400 hover:text-slate-800 transition-colors flex items-center gap-1 text-sm font-bold uppercase tracking-wider z-20"
         >
           <ArrowLeft size={14} /> Back
         </button>

         {/* Progress Header */}
         <div className="mb-8 mt-8">
            <div className="flex justify-end items-end mb-2">
               <span className="text-xs font-black uppercase tracking-widest text-slate-400 mr-2">Progress</span>
               <span className="text-sm font-bold text-slate-900">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full border border-black/10 overflow-hidden">
               <motion.div 
                 className="h-full bg-emerald-500"
                 initial={{ width: 0 }}
                 animate={{ width: `${progress}%` }}
                 transition={{ duration: 0.5, ease: "easeInOut" }}
               />
            </div>
         </div>

         {/* Question & Options */}
         <AnimatePresence mode="wait">
            <motion.div
               key={activeQuestion.id}
               initial={{ opacity: 0, x: 20 }}
               animate={{ opacity: 1, x: 0 }}
               exit={{ opacity: 0, x: -20 }}
               transition={{ duration: 0.3, ease: "easeOut" }}
               className="flex-grow flex flex-col justify-center"
            >
               <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-8 leading-tight">
                  {activeQuestion.text}
               </h3>

               <div className="space-y-3">
                  {activeQuestion.options.map((opt, idx) => (
                     <motion.button
                        key={opt.id}
                        onClick={() => onAnswer(activeQuestion.id, opt.id)}
                        whileHover={{ scale: 1.01, backgroundColor: "#F0FDF4", borderColor: "#10B981" }}
                        whileTap={{ scale: 0.98 }}
                        className="group w-full text-left p-4 md:p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-500 bg-white transition-all duration-200 relative overflow-hidden"
                     >
                        <div className="flex items-start gap-4 relative z-10">
                           <div className="w-7 h-7 rounded-full border-2 border-slate-300 group-hover:border-emerald-500 group-hover:bg-emerald-500 group-hover:text-white text-slate-400 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 transition-colors">
                              {String.fromCharCode(65 + idx)}
                           </div>
                           <span className="font-bold text-lg text-slate-700 group-hover:text-slate-900">
                              {opt.text}
                           </span>
                        </div>
                     </motion.button>
                  ))}
               </div>
            </motion.div>
         </AnimatePresence>
      </div>

      {/* RIGHT: Visual Context (Desktop Only) */}
      <div className="hidden lg:flex w-[45%] bg-slate-100 border-l-2 border-black items-center justify-center relative overflow-hidden">
         <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:20px_20px]" />
         <VisualContext questionId={activeQuestion.id} trackId={trackId} />
      </div>

    </div>
  );
};